import React, { useState, useEffect, useRef } from 'react';
import { FaPhone, FaFax, FaEnvelope, FaArrowLeft, FaCheck, FaTimes, FaLink, FaPrint, FaBuilding, FaPaypal  } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import KruozAPI from '../../KruozAPI';
import { useReactToPrint } from 'react-to-print';
import PayPalPayment from '../common/PayPalPayment'; 

// Format date from ISO string to a more readable format
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr; // Return original if invalid
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Modal component
const Modal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        <p className="mb-6">{message}</p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const QuotationView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [quotation, setQuotation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, type: null });
  const printableContentRef = useRef();
  const [showPaymentOption, setShowPaymentOption] = useState(false);

  useEffect(() => {
    console.log("useEffect triggered with id:", id);
    loadQuotation();
  }, [id]);

  const loadQuotation = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log("Fetching quotation with id:", id);
      const response = await KruozAPI.get(`/Quotation/Quotation/${id}`);
      console.log("API response:", response);
      const quote = response.data;
      quote.items = JSON.parse(quote.items);
      setQuotation(quote);
    } catch (error) {
      console.error('Error loading quotation:', error);
      setError('Failed to load quotation. Please try again.');
      toast.error('Failed to load quotation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    setModalConfig({
      isOpen: true,
      type: 'accept',
      title: 'Accept Quotation',
      message: 'Are you sure you want to accept this quotation? You will be able to change this decision later if needed.'
    });
  };

  const handleIgnore = async () => {
    setModalConfig({
      isOpen: true,
      type: 'ignore',
      title: 'Ignore Quotation',
      message: 'Are you sure you want to ignore this quotation?'
    });
  };

  const handleConfirm = async () => {
    const action = modalConfig.type === 'accept' ? 'Accepted' : 'Ignored';
    toast.promise(
      KruozAPI.post(`/Quotation/Status?quotationNumber=${quotation.quotationNumber}&status=${action}`),
      {
        loading: `${action === 'Accepted' ? 'Accepting' : 'Ignoring'} quotation...`,
        success: `Quote ${action.toLowerCase()} successfully!${action === 'Accepted' ? ' You can change this decision later if needed.' : ''}`,
        error: `Failed to ${action.toLowerCase()} quotation. Please try again.`,
      }
    ).then(() => {
      setQuotation({ ...quotation, status: action });
    });
    setModalConfig({ isOpen: false, type: null });
  };
  const handlePrint = useReactToPrint({
    content: () => printableContentRef.current,
  });

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  }

  if (!quotation) {
    return <div className="flex justify-center items-center h-screen">No Quote found.</div>;
  }

  const CompanyLogo = ({ logo, companyName }) => {
    const [imageError, setImageError] = useState(false);

    if (!logo || imageError) {
      return (
        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
          <FaBuilding className="text-gray-400 w-8 h-8" />
        </div>
      );
    }

    return (
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
        <img 
          src={process.env.REACT_APP_IMAGE_BASE_URL + logo} 
          alt={`${companyName} logo`}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    );
  };

  const handlePaymentSuccess = (details) => {
    // Here you would typically update your backend about the successful payment
    toast.success(`Payment completed. Thank you, ${details.payer.name.given_name}!`);
    // You might want to update the quotation status or redirect the user
  };

  const handlePaymentError = (error) => {
    toast.error("Payment failed. Please try again.");
    console.error("PayPal error:", error);
  };

  const PrintableContent = () => (
    <div className="bg-white p-4 sm:p-8 rounded-lg shadow-lg max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start mb-8">
      <div className="mb-4 sm:mb-0">
          <CompanyLogo logo={quotation.byCompanyLogo} companyName={quotation.byCompanyName} />
          <div className="mt-4">
            <h2 className="font-bold">{quotation.byCompanyName}</h2>
            <p>{quotation.byCompnayAddress}</p>
            <div className="flex items-center mt-1">
              <FaPhone className="mr-2 text-green-600" />
              <span>{quotation.byCompanyPhone}</span>
            </div>
            {quotation.byCompanyFax && (
              <div className="flex items-center mt-1">
                <FaFax className="mr-2 text-green-600" />
                <span>{quotation.byCompanyFax}</span>
              </div>
            )}
            <div className="flex items-center mt-1">
              <FaEnvelope className="mr-2 text-green-600" />
              <span>{quotation.byCompanyEmail}</span>
            </div>
            <div className="flex items-center mt-1">
              <FaLink className="mr-2 text-green-600" />
              <span>{quotation.byCompanyWebSite}</span>
            </div>
          </div>
        </div>        <div className="w-full sm:w-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-green-700 mb-4">Quotation</h1>
          <div className="grid grid-cols-2 gap-2">
            <span className="font-semibold">Date:</span>
            <span>{formatDate(quotation.createdDate)}</span>
            <span className="font-semibold">Quote#:</span>
            <span>{quotation.quotationNumber}</span>
            <span className="font-semibold">Valid Until:</span>
            <span>{formatDate(quotation.expiredDate)}</span>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-semibold bg-green-700 text-white p-2 mb-2">Customer</h3>
        <p>{quotation.customerName}</p>
        <p>{quotation.customerAddress}</p>
        <p>{quotation.customerMobile}</p>
        <p>{quotation.customerEmail}</p>
      </div>

      <div className="overflow-x-auto mb-8">
        <table className="w-full">
          <thead>
            <tr className="bg-green-700 text-white">
              <th className="p-2 text-left">Item#</th>
              <th className="p-2 text-left">Description</th>
              <th className="p-2 text-right">Quantity</th>
              <th className="p-2 text-right">Unit Price</th>
              <th className="p-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {quotation.items.map((item, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                <td className="p-2">{item.id}</td>
                <td className="p-2">{item.text}</td>
                <td className="p-2 text-right">{1}</td>
                <td className="p-2 text-right">${item.price}</td>
                <td className="p-2 text-right">${item.price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row justify-between">
        <div className="w-full sm:w-1/2 mb-4 sm:mb-0">
          <h3 className="text-lg font-semibold bg-green-700 text-white p-2 mb-2">Notes or Special Comments:</h3>
          <p className="p-2 border border-gray-300 rounded">{quotation.notes}</p>
        </div>
        <div className="w-full sm:w-1/3">
          <div className="grid grid-cols-2 gap-2">
            <span className="font-semibold">Sub Total:</span>
            <span className="text-right">${quotation.subTotal.toFixed(2)}</span>
            <span className="font-semibold">Discount:</span>
            <span className="text-right">${quotation.discountAmount.toFixed(2)}</span>
            <span className="font-semibold">GST:</span>
            <span className="text-right">${quotation.gstAmount.toFixed(2)}</span>
            <span className="font-semibold">Shipping Cost:</span>
            <span className="text-right">${quotation.transportCharge.toFixed(2)}</span>
            <span className="font-semibold">Amount:</span>
            <span className="text-right">${quotation.amount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <p className="mt-8 text-sm">
        We will be happy to supply any further information you may need and trust that you call on us to fill your order, which will receive our prompt and careful attention.
      </p>

      <div className="mt-8 text-center text-sm text-gray-500">
        Thank You For Your Business!
      </div>
      
      {quotation.status === 'Accepted' && (
        <div className="mt-4 text-center text-sm text-green-600">
          This Quote has been accepted.
        </div>
      )}
      {quotation.status === 'Ignored' && (
        <div className="mt-4 text-center text-sm text-gray-600">
          This Quote has been ignored.
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-gray-100 min-h-screen py-4 sm:py-8">
      <Toaster position="top-right" />
      <div className="bg-white p-4 sm:p-8 rounded-lg shadow-lg max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4 sm:mb-0"
          >
            <FaArrowLeft className="mr-2" /> To Request
          </button>
          <div className="flex flex-wrap gap-2 sm:gap-4">
            {quotation.status === 'Accepted' && !showPaymentOption && (
              <button
                onClick={() => setShowPaymentOption(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-3 sm:px-4 rounded-full flex items-center text-sm sm:text-base"
              >
                <FaPaypal className="mr-1 sm:mr-2" /> Pay with PayPal
              </button>
            )}
            <button
              onClick={handlePrint}
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-3 sm:px-4 rounded-full flex items-center text-sm sm:text-base"
            >
              <FaPrint className="mr-1 sm:mr-2" /> Print
            </button>
            {quotation.status !== 'Accepted' && quotation.status !== 'Ignored' && (
              <button
                onClick={handleIgnore}
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-3 sm:px-4 rounded-full flex items-center text-sm sm:text-base"
              >
                <FaTimes className="mr-1 sm:mr-2" /> Ignore
              </button>
            )}
            {quotation.status !== 'Accepted' && (
              <button
                onClick={handleAccept}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-3 sm:px-4 rounded-full flex items-center text-sm sm:text-base"
              >
                <FaCheck className="mr-1 sm:mr-2" /> Accept
              </button>
            )}
          </div>
        </div>
        {showPaymentOption && (
          <div className="mt-8 bg-gray-100 p-4 rounded-lg">
            <h3 className="text-xl font-bold mb-4">Payment Details</h3>
            <p className="mb-4">Total Amount: ${quotation.amount.toFixed(2)}</p>
            <PayPalPayment
              amount={quotation.amount}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
            <p className="mt-4 text-sm text-gray-600">
              Complete your payment securely through PayPal.
            </p>
          </div>
        )}
        <div ref={printableContentRef}>
          <PrintableContent />
        </div>

        
      </div>

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig({ isOpen: false, type: null })}
        onConfirm={handleConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
};

export default QuotationView;